// Automation (contracts/lume.md §1): everything Lume says. Push triggers (come-backs, Review due, weekly Focus)
// with their switch (config lume.pushTriggers, admin), lines and sends; in-app lines by trigger. Editors add,
// edit, turn on/off and delete lines; the line editor fills placeholders with sample values in its preview.

import { useRef, useState } from "react";
import { type LineContent, type LineDraft, type LumeLine, type TriggerSpec, useBulkLines, useCreateLine, useDeleteLine, useLumeLines, useUpdateLine } from "../api/lume";
import { LOCALE_LABEL, type Locale, RTL_LOCALES } from "../api/messaging";
import { useConfig, useSetConfig } from "../api/queries";
import { useAuth, useCan } from "../auth/AuthContext";
import { Button, Card, Chip, ConfirmDialog, Dialog, ErrorState, Field, InlineError, Loading, PageHeader, Segmented, Select, TextInput } from "../components/ui";
import { fmtInt } from "../lib/format";
import {
  APP_MIN_LINES,
  fillSample,
  LINE_LIMITS,
  type LumePushTriggers,
  type Mood,
  MOOD_LABEL,
  moodSrc,
  MOODS,
  PUSH_SWITCH,
  placeholderMessage,
  readLumeConfig,
  SAMPLE_VALUES,
  triggerLabel,
  unknownPlaceholders,
} from "../lib/lume";
import { resolveEntry, toLocalized } from "../lib/messaging";
import a from "./automation.module.css";
import { ClaimsNote } from "./learn/components";
import { ContentEditor, type ContentState, type FieldSpec, Switch, SwitchRow, textState } from "./messaging/components";
import m from "./messaging/messaging.module.css";
import n from "./notifications.module.css";
import styles from "./pages.module.css";

const FIELDS = ["title", "text"] as const;
type Key = (typeof FIELDS)[number];
const SPECS: Record<"push" | "app", readonly FieldSpec<Key>[]> = {
  push: [
    { key: "title", label: "Push title", limit: LINE_LIMITS.title, placeholder: "e.g. Lume misses you" },
    { key: "text", label: "Push text", limit: LINE_LIMITS.text, rows: 3, placeholder: "e.g. {days} days without a puzzle. One takes two minutes." },
  ],
  app: [{ key: "text", label: "Line", limit: LINE_LIMITS.text, rows: 3, placeholder: "e.g. Good morning! Today's puzzle is ready." }],
};
const SWITCH_LABEL: Record<keyof LumePushTriggers, string> = { comeback: "Come-back pushes (2, 5 and 12 days)", review_due: "Review due", focus_week: "Weekly Focus" };

type Editing = { line: LumeLine } | { line: null; channel: "push" | "app"; trigger: string };

export function AutomationPage() {
  const canEdit = useCan("lume.edit");
  const canConfigure = useCan("config.edit");
  const { admin } = useAuth();
  const lines = useLumeLines();
  const config = useConfig();
  const setConfig = useSetConfig();
  const bulk = useBulkLines();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toggle, setToggle] = useState<keyof LumePushTriggers | null>(null);
  const [q, setQ] = useState("");

  const switches = readLumeConfig(config.data?.values).pushTriggers;
  const items = lines.data?.items ?? [];
  const triggers = lines.data?.triggers ?? [];
  const needle = q.trim().toLowerCase();
  const shown = needle ? items.filter((l) => JSON.stringify(l.content).toLowerCase().includes(needle) || l.trigger.includes(needle)) : items;
  const byTrigger = (t: string) => shown.filter((l) => l.trigger === t);
  const activeCount = (t: string) => items.filter((l) => l.trigger === t && l.active).length;

  const warnings = [
    ...triggers.filter((t) => t.channel === "push" && switches[PUSH_SWITCH[t.trigger]!] && activeCount(t.trigger) === 0).map((t) => `${triggerLabel(t.trigger)} is on but has no active line: nothing will send.`),
    ...triggers.filter((t) => t.channel === "app" && activeCount(t.trigger) < APP_MIN_LINES).map((t) => `${triggerLabel(t.trigger)} has ${activeCount(t.trigger)} active ${activeCount(t.trigger) === 1 ? "line" : "lines"}: players will see repeats.`),
  ];

  const select = (id: string, on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const runBulk = (op: "activate" | "deactivate" | "delete") =>
    bulk.mutate(
      { ids: [...selected], op },
      {
        onSuccess: () => {
          setSelected(new Set());
          setConfirmDelete(false);
        },
      },
    );

  const row = (l: LumeLine) => (
    <LineRow
      key={l.id}
      line={l}
      canEdit={canEdit}
      selected={selected.has(l.id)}
      onSelect={(on) => select(l.id, on)}
      onOpen={() => setEditing({ line: l })}
      onActive={(on) => bulk.mutate({ ids: [l.id], op: on ? "activate" : "deactivate" })}
      busy={bulk.isPending}
    />
  );

  return (
    <>
      <PageHeader
        title="Automation"
        meta={
          canEdit ? (
            <span>Everything Lume says: pushes that bring players back without repeating, and the lines the app shows. Lines are written per language; every change is audited.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · editing lines needs the editor role (you are {admin?.role})
            </span>
          )
        }
        actions={
          canEdit ? (
            <Button variant="primary" onClick={() => setEditing({ line: null, channel: "app", trigger: "open_morning" })} data-testid="new-line">
              New line
            </Button>
          ) : null
        }
      />
      {lines.isPending || config.isPending ? (
        <Card>
          <Loading rows={8} />
        </Card>
      ) : lines.isError ? (
        <ErrorState error={lines.error} onRetry={() => void lines.refetch()} />
      ) : (
        <div className={a.stack}>
          {warnings.length ? (
            <div className={m.notice} data-tone="warning" data-testid="line-warnings">
              <strong>
                {warnings.length} {warnings.length === 1 ? "thing needs" : "things need"} attention
              </strong>
              <ul className={a.warnList}>
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className={a.toolbar}>
            <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lines" data-search aria-label="Search lines" className={a.search} />
            {canEdit && selected.size ? (
              <div className={a.bulk} data-testid="bulk-bar">
                <span className="mono">{selected.size} selected</span>
                <Button size="sm" onClick={() => runBulk("activate")} busy={bulk.isPending}>
                  Turn on
                </Button>
                <Button size="sm" onClick={() => runBulk("deactivate")} busy={bulk.isPending}>
                  Turn off
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                  Delete…
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </div>
            ) : null}
          </div>
          <InlineError error={bulk.error ?? setConfig.error} />

          <section aria-label="Push triggers">
            <h2 className={a.sectionTitle}>Push triggers</h2>
            <p className={a.sectionNote}>Sent by the server in each player's evening, at most once per stage; quiet hours, the daily cap and the reminder opt-out apply. A line never repeats while the trigger has an unused one.</p>
            <div className={a.pushGrid}>
              {triggers
                .filter((t) => t.channel === "push")
                .map((t) => {
                  const sw = PUSH_SWITCH[t.trigger]!;
                  const on = switches[sw];
                  const list = byTrigger(t.trigger);
                  const active = activeCount(t.trigger);
                  const sends = items.filter((l) => l.trigger === t.trigger).reduce((s, l) => s + l.sends30d, 0);
                  return (
                    <Card
                      key={t.trigger}
                      title={triggerLabel(t.trigger)}
                      actions={
                        <span className={a.switchCell} title={canConfigure ? undefined : "Turning triggers on or off needs the admin role (config lume.pushTriggers)"}>
                          <Chip tone={on ? "success" : "muted"}>{on ? "on" : "off"}</Chip>
                          <Switch checked={on} onChange={() => setToggle(sw)} label={`${on ? "Turn off" : "Turn on"} ${SWITCH_LABEL[sw]}`} disabled={!canConfigure || setConfig.isPending} testId={`switch-${t.trigger}`} />
                        </span>
                      }
                    >
                      <div className={a.triggerBody} data-testid={`push-${t.trigger}`}>
                        <p className={a.description}>
                          {t.description} <Placeholders spec={t} />
                          {sw === "comeback" ? <span className={styles.faint}> One switch for the three come-backs.</span> : null}
                        </p>
                        <div className={a.triggerStats}>
                          <span>
                            <strong className="mono">{active}</strong> active of {items.filter((l) => l.trigger === t.trigger).length}
                          </span>
                          <span>
                            <strong className="mono">{fmtInt(sends)}</strong> sent in 30 days
                          </span>
                        </div>
                        {on && active === 0 ? (
                          <p className={a.warn} data-testid="no-active-line">
                            On, but no active line: nothing will send.
                          </p>
                        ) : null}
                        <ul className={a.lineList}>{list.map(row)}</ul>
                        {canEdit ? (
                          <Button size="sm" variant="ghost" onClick={() => setEditing({ line: null, channel: "push", trigger: t.trigger })}>
                            + Line
                          </Button>
                        ) : null}
                      </div>
                    </Card>
                  );
                })}
            </div>
          </section>

          <section aria-label="In-app lines">
            <h2 className={a.sectionTitle}>In-app lines</h2>
            <p className={a.sectionNote}>The app picks these itself and never repeats one of the last {APP_MIN_LINES} it showed for a trigger, so each trigger wants at least {APP_MIN_LINES} active lines.</p>
            <div className={a.groups}>
              {triggers
                .filter((t) => t.channel === "app")
                .map((t) => {
                  const list = byTrigger(t.trigger);
                  const active = activeCount(t.trigger);
                  return (
                    <div key={t.trigger} className={a.group} data-testid={`app-${t.trigger}`}>
                      <div className={a.groupHead}>
                        <div>
                          <strong>{triggerLabel(t.trigger)}</strong> <span className="mono">{t.trigger}</span>
                          <p className={a.description}>
                            {t.description} <Placeholders spec={t} />
                          </p>
                        </div>
                        <div className={a.groupSide}>
                          <Chip tone={active < APP_MIN_LINES ? "warning" : "success"}>{active} active</Chip>
                          {canEdit ? (
                            <Button size="sm" variant="ghost" onClick={() => setEditing({ line: null, channel: "app", trigger: t.trigger })}>
                              + Line
                            </Button>
                          ) : null}
                        </div>
                      </div>
                      {active < APP_MIN_LINES ? (
                        <p className={a.warn} data-testid="few-lines">
                          Fewer than {APP_MIN_LINES} active lines: players will see repeats.
                        </p>
                      ) : null}
                      {list.length ? <ul className={a.lineList}>{list.map(row)}</ul> : null}
                    </div>
                  );
                })}
            </div>
          </section>
        </div>
      )}

      {editing ? <LineDialog key={editing.line?.id ?? "new"} editing={editing} triggers={triggers} canEdit={canEdit} onClose={() => setEditing(null)} /> : null}

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${selected.size} ${selected.size === 1 ? "line" : "lines"}?`}
        confirmLabel="Delete"
        tone="danger"
        busy={bulk.isPending}
        error={bulk.error}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => runBulk("delete")}
      >
        <p className={styles.prose}>They stop at once and their send history goes with them. To keep them for later, turn them off instead.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={toggle !== null}
        title={toggle ? `${switches[toggle] ? "Turn off" : "Turn on"} ${SWITCH_LABEL[toggle]}?` : ""}
        confirmLabel={toggle && switches[toggle] ? "Turn off" : "Turn on"}
        busy={setConfig.isPending}
        error={setConfig.error}
        onCancel={() => setToggle(null)}
        onConfirm={() =>
          toggle &&
          setConfig.mutate({ key: "lume.pushTriggers", value: { ...switches, [toggle]: !switches[toggle] } }, { onSuccess: () => setToggle(null) })
        }
      >
        <p className={styles.prose}>
          Writes the config key <code>lume.pushTriggers</code>. {toggle && switches[toggle] ? "No more of these pushes go out from the next run." : "These pushes start with the next run of the notification job."}
        </p>
      </ConfirmDialog>
    </>
  );
}

function Placeholders({ spec }: { spec: TriggerSpec }) {
  if (!spec.placeholders.length) return null;
  return (
    <span className={a.placeholders}>
      {spec.placeholders.map((p) => (
        <code key={p}>{`{${p}}`}</code>
      ))}
    </span>
  );
}

function LineRow({
  line,
  canEdit,
  selected,
  onSelect,
  onOpen,
  onActive,
  busy,
}: {
  line: LumeLine;
  canEdit: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onOpen: () => void;
  onActive: (on: boolean) => void;
  busy: boolean;
}) {
  const en = line.content.en;
  const langs = Object.keys(line.content).length;
  return (
    <li className={a.line} data-active={line.active || undefined} data-testid="line">
      {canEdit ? <input type="checkbox" checked={selected} onChange={(e) => onSelect(e.target.checked)} aria-label={`Select “${en?.text ?? ""}”`} className={a.check} /> : null}
      {line.mood ? <img src={moodSrc(line.mood as Mood)} alt={line.mood} className={a.lineMood} /> : <span className={a.lineMood} aria-hidden />}
      <button type="button" className={a.lineText} onClick={onOpen}>
        {en?.title ? <strong>{en.title}</strong> : null}
        <span>{en?.text}</span>
      </button>
      <span className={a.lineMeta}>
        {langs} {langs === 1 ? "lang" : "langs"}
        {line.weight > 1 ? ` · ×${line.weight}` : ""}
        {line.channel === "push" ? ` · ${fmtInt(line.sends30d)} sent` : ""}
      </span>
      {canEdit ? (
        <Switch checked={line.active} onChange={onActive} label={`${line.active ? "Turn off" : "Turn on"} “${en?.text ?? ""}”`} disabled={busy} testId="line-active" />
      ) : (
        <Chip tone={line.active ? "success" : "muted"}>{line.active ? "on" : "off"}</Chip>
      )}
    </li>
  );
}

// ---------------------------------------------------------------- the line editor

function LineDialog({ editing, triggers, canEdit, onClose }: { editing: Editing; triggers: TriggerSpec[]; canEdit: boolean; onClose: () => void }) {
  const line = editing.line;
  const create = useCreateLine();
  const update = useUpdateLine();
  const remove = useDeleteLine();
  const start = editing.line ? { channel: editing.line.channel, trigger: editing.line.trigger } : { channel: editing.channel, trigger: editing.trigger };
  const [channel, setChannel] = useState<"push" | "app">(start.channel);
  const [trigger, setTrigger] = useState(start.trigger);
  const [text, setText] = useState<ContentState<Key>>(() => textState(FIELDS, line?.content));
  const [locale, setLocale] = useState<Locale>("en");
  const [mood, setMood] = useState<Mood | null>((line?.mood as Mood | null) ?? null);
  const [weight, setWeight] = useState(String(line?.weight ?? 1));
  const [active, setActive] = useState(line?.active ?? true);
  const [deleting, setDeleting] = useState(false);
  const focused = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const save = line ? update : create;

  const spec = triggers.find((t) => t.trigger === trigger);
  const allowed = spec?.placeholders ?? [];
  const fields = SPECS[channel];
  const en = text.draft.en;
  const weightN = Number(weight);
  /** The first placeholder the trigger doesn't allow, in any language: the API's 400, before sending. */
  const badPlaceholder = (() => {
    for (const [loc, e] of Object.entries(text.draft) as [Locale, Record<Key, string>][]) {
      const used = unknownPlaceholders(`${channel === "push" ? e.title : ""} ${e.text}`, allowed);
      if (used[0]) return placeholderMessage(used[0], trigger, loc, allowed);
    }
    return null;
  })();
  const problem = !spec
    ? "Pick a trigger."
    : channel === "push" && !en.title.trim()
      ? "A push line needs an English title."
      : !en.text.trim()
        ? "Write the English text."
        : (badPlaceholder ?? (!Number.isInteger(weightN) || weightN < 1 || weightN > 10 ? "Weight: a whole number from 1 to 10." : null));

  /** Inserts `{name}` at the caret of the last focused field (else at the end of the text), in the current language. */
  const insert = (name: string) => {
    const token = `{${name}}`;
    const el = focused.current;
    const field: Key = el?.dataset.testid === "field-title" && channel === "push" ? "title" : "text";
    const value = text.draft[locale][field];
    const start = el && document.body.contains(el) ? (el.selectionStart ?? value.length) : value.length;
    const end = el && document.body.contains(el) ? (el.selectionEnd ?? value.length) : value.length;
    const next = (value.slice(0, start) + token + value.slice(end)).slice(0, field === "title" ? LINE_LIMITS.title : LINE_LIMITS.text);
    setText((s) => ({ ...s, draft: { ...s.draft, [locale]: { ...s.draft[locale], [field]: next } } }));
    requestAnimationFrame(() => {
      if (el && document.body.contains(el)) {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      }
    });
  };

  const submit = () => {
    const keys = channel === "push" ? FIELDS : (["text"] as const);
    const draft: LineDraft = {
      channel,
      trigger,
      content: toLocalized(text.draft, keys).content as LineContent,
      mood,
      weight: weightN,
      active,
    };
    if (line) update.mutate({ id: line.id, patch: draft }, { onSuccess: onClose });
    else create.mutate(draft, { onSuccess: onClose });
  };

  const shown = resolveEntry(text.draft, locale);
  const rtl = RTL_LOCALES.has(locale) ? "rtl" : undefined;
  return (
    <Dialog
      open
      title={line ? (canEdit ? "Edit line" : "Line") : "New line"}
      onClose={onClose}
      width={1000}
      footer={
        canEdit ? (
          <>
            <div className={a.footLeft}>
              {line ? (
                <Button variant="ghost" onClick={() => setDeleting(true)} disabled={save.isPending}>
                  Delete…
                </Button>
              ) : null}
              {problem ? (
                <span className={a.footProblem} data-testid="line-problem">
                  {problem}
                </span>
              ) : null}
            </div>
            <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button variant="primary" busy={save.isPending} disabled={problem !== null} onClick={submit} data-testid="line-save">
              {line ? "Save changes" : "Add line"}
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      <div className={a.editor}>
        <fieldset
          className={a.form}
          disabled={!canEdit || save.isPending}
          onFocus={(e) => {
            const t = e.target;
            if ((t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) && t.dataset.testid?.startsWith("field-")) focused.current = t;
          }}
        >
          <div className={a.twoUp}>
            <Field label="Channel">
              <Segmented
                label="Channel"
                value={channel}
                onChange={(c) => {
                  setChannel(c);
                  setTrigger(triggers.find((t) => t.channel === c)?.trigger ?? "");
                }}
                options={[
                  { value: "push", label: "Push" },
                  { value: "app", label: "In-app" },
                ]}
              />
            </Field>
            <Field label="Trigger" hint={spec?.description}>
              <Select value={trigger} onChange={(e) => setTrigger(e.target.value)} data-testid="line-trigger">
                {triggers
                  .filter((t) => t.channel === channel)
                  .map((t) => (
                    <option key={t.trigger} value={t.trigger}>
                      {triggerLabel(t.trigger)} · {t.trigger}
                    </option>
                  ))}
              </Select>
            </Field>
          </div>
          <div className={a.chips} data-testid="placeholder-chips">
            <span className="monoLabel">Placeholders</span>
            {allowed.length ? (
              allowed.map((p) => (
                <button key={p} type="button" className={a.chip} onMouseDown={(e) => e.preventDefault()} onClick={() => insert(p)} title={`Insert at the cursor · preview: ${SAMPLE_VALUES[p] ?? "?"}`}>
                  {`{${p}}`}
                </button>
              ))
            ) : (
              <span className={styles.faint}>none for this trigger</span>
            )}
          </div>
          {badPlaceholder ? (
            <div className={m.notice} data-tone="danger" data-testid="placeholder-problem">
              <strong>{badPlaceholder}</strong>
              <span>A line whose placeholder has no value is skipped, so the API refuses it.</span>
            </div>
          ) : null}
          <ContentEditor
            value={text}
            onChange={setText}
            fields={fields}
            englishRequired={channel === "push" ? ["title", "text"] : ["text"]}
            locale={locale}
            onLocale={setLocale}
            note={<ClaimsNote>Lume talks about practice, accuracy, speed and how reasoning works. Never promise a higher IQ, never give medical advice, never reveal daily answers.</ClaimsNote>}
          />
          <Field label="Pose" hint={mood ? MOOD_LABEL[mood] : "No pose: the app uses its default for the moment."}>
            <div className={a.moods} role="radiogroup" aria-label="Pose">
              <button type="button" role="radio" aria-checked={mood === null} className={a.mood} onClick={() => setMood(null)}>
                <span className={a.moodNone}>none</span>
              </button>
              {MOODS.map((x) => (
                <button key={x} type="button" role="radio" aria-checked={mood === x} aria-label={x} className={a.mood} onClick={() => setMood(x)} data-testid={`mood-${x}`}>
                  <img src={moodSrc(x)} alt="" />
                  <span>{x}</span>
                </button>
              ))}
            </div>
          </Field>
          <div className={a.twoUp}>
            <Field label="Weight" hint="1–10: how often it comes up against the trigger's other lines.">
              <TextInput type="number" min={1} max={10} step={1} value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="line-weight" />
            </Field>
            <SwitchRow title="Active" hint="Off: kept, but never sent or shown." checked={active} onChange={setActive} label="Active" testId="line-active-field" />
          </div>
          <InlineError error={save.error} />
        </fieldset>

        <aside className={a.previewSide} data-testid="line-preview">
          <div className={m.previewHead}>
            <span className="monoLabel">Preview · {LOCALE_LABEL[locale]}</span>
            {shown.fallback.length ? <span className={m.fallbackNote}>English for: {shown.fallback.join(", ")}</span> : null}
          </div>
          {channel === "push" ? (
            <div className={n.push} aria-label="Push preview" dir={rtl}>
              <span className={n.icon} aria-hidden>
                IQ
              </span>
              <div className={n.pushText}>
                <div className={n.pushHead}>
                  <strong>{fillSample(shown.title) || "Title"}</strong>
                  <span>now</span>
                </div>
                <p>{fillSample(shown.text) || "Text"}</p>
              </div>
            </div>
          ) : (
            <div className={a.lume} aria-label="In-app preview" dir={rtl}>
              <img src={moodSrc(mood ?? "idle")} alt={`Lume, ${mood ?? "idle"}`} className={a.lumeImage} />
              <div className={a.bubble}>{fillSample(shown.text) || "Lume's line"}</div>
            </div>
          )}
          <p className={a.sample}>
            Sample values:{" "}
            {Object.entries(SAMPLE_VALUES)
              .filter(([k]) => allowed.includes(k))
              .map(([k, v]) => `{${k}} = ${v}`)
              .join(" · ") || "none for this trigger"}
            .
          </p>
          {line?.channel === "push" ? <p className={a.sample}>{fmtInt(line.sends30d)} players got this line in the last 30 days.</p> : null}
        </aside>
      </div>
      <ConfirmDialog
        open={deleting}
        title="Delete this line?"
        confirmLabel="Delete line"
        tone="danger"
        busy={remove.isPending}
        error={remove.error}
        onCancel={() => setDeleting(false)}
        onConfirm={() => line && remove.mutate(line.id, { onSuccess: onClose })}
      >
        <p className={styles.prose}>“{en.text}” stops at once. To keep it for later, turn it off instead.</p>
      </ConfirmDialog>
    </Dialog>
  );
}
